import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize ScaleDialog to prevent unnecessary re-renders
const ScaleDialog = React.memo(({ open, onClose, canvas, selectedObject }) => {
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const [prevScaleX, setPrevScaleX] = useState(1);
  const [prevScaleY, setPrevScaleY] = useState(1);
  const [isDirty, setIsDirty] = useState(false);
  const [inputScaleX, setInputScaleX] = useState("");
  const [inputScaleY, setInputScaleY] = useState("");
  const selectedObjectRef = useRef(selectedObject);

  // Update ref when selectedObject changes
  useEffect(() => {
    selectedObjectRef.current = selectedObject;
    console.log("[ScaleDialog] selectedObject updated:", selectedObject);
  }, [selectedObject]);

  // Initialize scale values when dialog opens or selected object changes
  useEffect(() => {
    if (selectedObject && open) {
      const currentScaleX = Number(selectedObject.get("scaleX")) || 1;
      const currentScaleY = Number(selectedObject.get("scaleY")) || 1;
      setScaleX(currentScaleX);
      setScaleY(currentScaleY);
      setInputScaleX(Math.round(currentScaleX * 100).toString());
      setInputScaleY(Math.round(currentScaleY * 100).toString());
      setPrevScaleX(currentScaleX);
      setPrevScaleY(currentScaleY);
      setIsDirty(false);
      console.log(
        "[ScaleDialog] Initialized scaleX:",
        currentScaleX,
        "scaleY:",
        currentScaleY
      );
    }
  }, [selectedObject, open]);

  // Memoize update callbacks
  const updateScaleXCallback = useCallback(
    (value) => {
      const roundedValue = value / 100;
      setScaleX(roundedValue);
      setInputScaleX(Math.round(roundedValue * 100).toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ scaleX: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[ScaleDialog] Updated scaleX:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[ScaleDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  const updateScaleYCallback = useCallback(
    (value) => {
      const roundedValue = value / 100;
      setScaleY(roundedValue);
      setInputScaleY(Math.round(roundedValue * 100).toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ scaleY: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[ScaleDialog] Updated scaleY:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[ScaleDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  // Create debounced update functions
  const updateScaleX = useDebouncedCallback(updateScaleXCallback, 100, canvas);
  const updateScaleY = useDebouncedCallback(updateScaleYCallback, 100, canvas);

  // Handle dialog close (cancel)
  const handleCancel = useCallback(() => {
    if (isDirty && selectedObject) {
      selectedObject.set({ scaleX: prevScaleX, scaleY: prevScaleY });
      selectedObject.setCoords();
      canvas?.requestRenderAll();
      console.log(
        "[ScaleDialog] Canceled, reverted to scaleX:",
        prevScaleX,
        "scaleY:",
        prevScaleY
      );
    }
    onClose();
  }, [isDirty, selectedObject, prevScaleX, prevScaleY, canvas, onClose]);

  // Handle apply scale (save changes and add to history)
  const handleApply = useCallback(() => {
    updateScaleX.flush();
    updateScaleY.flush();
    if (isDirty && canvas && selectedObject) {
      canvas.setActiveObject(selectedObject);
      selectedObject.set({ scaleX, scaleY });
      selectedObject.setCoords();
      canvas.requestRenderAll();
      canvas.fire("object:modified", { target: selectedObject });
      console.log(
        "[ScaleDialog] Applied scaleX:",
        scaleX,
        "scaleY:",
        scaleY,
        "Fired object:modified for:",
        selectedObject
      );
    } else {
      console.log(
        "[ScaleDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvas,
        "selectedObject=",
        !!selectedObject
      );
    }
    setIsDirty(false);
    onClose();
  }, [
    isDirty,
    canvas,
    selectedObject,
    scaleX,
    scaleY,
    updateScaleX,
    updateScaleY,
    onClose,
  ]);

  // Stop mouse events from propagating to allow input interaction
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent backdrop click from closing the dialog
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent keydown events (e.g., Escape key) from closing the dialog
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  // Handle input changes
  const handleScaleXChange = useCallback((e) => {
    setInputScaleX(e.target.value);
  }, []);

  const handleScaleXBlur = useCallback(() => {
    const value = Number(inputScaleX);
    if (!isNaN(value) && value >= 0) {
      updateScaleX(value);
    } else {
      setInputScaleX(Math.round(scaleX * 100).toString());
    }
  }, [inputScaleX, scaleX, updateScaleX]);

  const handleScaleYChange = useCallback((e) => {
    setInputScaleY(e.target.value);
  }, []);

  const handleScaleYBlur = useCallback(() => {
    const value = Number(inputScaleY);
    if (!isNaN(value) && value >= 0) {
      updateScaleY(value);
    } else {
      setInputScaleY(Math.round(scaleY * 100).toString());
    }
  }, [inputScaleY, scaleY, updateScaleY]);

  const handleScaleXStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateScaleX(value);
      }
    },
    [updateScaleX]
  );

  const handleScaleYStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateScaleY(value);
      }
    },
    [updateScaleY]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Scale Toolbar
      </DialogTitle>
      <DialogContent
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Stack direction="column" sx={{ marginTop: 1 }}>
          <TextField
            type="number"
            id="canvasScaleX"
            margin="dense"
            value={inputScaleX}
            onChange={handleScaleXChange}
            onBlur={handleScaleXBlur}
            onInput={handleScaleXStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1% for fine control
            }}
            label="Scale X"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Scale X in percentage" }}
          />
          <TextField
            type="number"
            id="canvasScaleY"
            margin="dense"
            value={inputScaleY}
            onChange={handleScaleYChange}
            onBlur={handleScaleYBlur}
            onInput={handleScaleYStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1% for fine control
            }}
            label="Scale Y"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Scale Y in percentage" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel scale changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply scale changes"
        >
          Apply Scale
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default ScaleDialog;
